import { ChevronLeft } from "@mui/icons-material";
import { Alert, Box, Snackbar, TextField, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { ChangeEvent, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EditIcon } from "../../../../../../../assets/images/icons/EditIcon.tsx";
import PlusIcon from "../../../../../../../assets/images/icons/PlusIcon.tsx";
import BriaIconButton from "../../../../../../../components/common/BriaIconButton/BriaIconButton.tsx";
import BriaTooltip from "../../../../../../../components/common/BriaTooltip/BriaTooltip.tsx";
import LoadingPlaceholder from "../../../../../../../components/common/LoadingPlaceholder/LoadingPlaceholder.tsx";
import { BRIA_PUBLIC_ORG_ID } from "../../../../../../../constants/OrgConstants.ts";
import { getSelectedOrganization } from "../../../../../../../helpers/localStorage.ts";
import { useAppStore } from "../../../../../../../hooks/useStores.tsx";
import InputLayout from "../../../../../../../layout/InputLayout/InputLayout.tsx";
import { defaultBrandDefinition } from "../../../../../../../models/brandDefinition.ts";
import { CampaignEntityStatus } from "../../../../../../../models/common.ts";
import useScenesUtils from "../../../../../../CustomUtils/ScenesUtils.tsx";
import useDesignEditorUtils from "../../../../../../CustomUtils/UseDesignEditor.tsx";
import StepperNavigation from "../../../../../../StepperNavigation/StepperNavigation.tsx";
import MainTabHeader from "../../../../../../components/common/MainTabHeader/MainTabHeader.tsx";
import { PopUpSteps } from "../../../../../../constants/mock-data.ts";
import { DesignEditorContext } from "../../../../../../contexts/DesignEditor.tsx";
import { useEditor } from "../../../../../../hooks/useEditor.tsx";
import { BrandDefinition } from "../../../../../../models/brandDefinition.ts";
import type { IScene } from "../../../../../../types";
import BrandItem from "./BrandItem/BrandItem.tsx";
import styles from "./Brands.module.scss";
import EditBrand from "./EditBrand/EditBrand.tsx";
const Brands = () => {
	const editor = useEditor();
	const { isPopupView, setScenes, setCurrentScene } = useContext(DesignEditorContext);
	const { brandsDefinitionStore, campaignStore, designEditorStore } = useAppStore();
	const [brands, setBrands] = useState<BrandDefinition[]>([]);
	const [publicBrands, setPublicBrands] = useState<BrandDefinition[]>([]);
	const { t } = useTranslation("translation", { keyPrefix: "editor.tabs.brands" });
	const [selectedBrand, setSelectedBrand] = useState<BrandDefinition | null>(defaultBrandDefinition);
	const [showSelectedBrand, setShowSelectedBrand] = useState<boolean>(false);
	const [isViewIconClicked, setIsViewIconClicked] = useState<boolean>(true);
	const [activeBrand, setActiveBrand] = useState<BrandDefinition | null>(null);
	const [successMessage, setSuccessMessage] = useState<string | null>(null);
	const isBrandTemplateEditor = campaignStore.isBrandTemplateEditor;
	const { applyBrandConfigration, applyBrandConfigrationOnScene } = useDesignEditorUtils();
	const { updateScenes } = useScenesUtils();
	const [showBrandSavingPopup, setShowBrandSavingPopup] = useState<boolean>(false);
	const BRANDS_INDEX = PopUpSteps.indexOf("brands");

	const setInternalBrands = () => {
		if (brandsDefinitionStore.brands) {
			setBrands(brandsDefinitionStore.brands.items.filter((item) => item.status === CampaignEntityStatus.ACTIVE));
		}
		if (brandsDefinitionStore.publicBrands) {
			setPublicBrands(
				brandsDefinitionStore.publicBrands.items.filter((item) => item.status === CampaignEntityStatus.ACTIVE),
			);
		}
		if (brandsDefinitionStore.globalSelectedBrand.id === brandsDefinitionStore.selectedBrand.id) {
			const updatedBrand = brandsDefinitionStore.brands.items.find(
				(item) => item.id === brandsDefinitionStore.selectedBrand.id,
			);
			if (updatedBrand) brandsDefinitionStore.globalSelectedBrand = updatedBrand;
		}
	};

	const setInternalPublicBrands = () => {
		if (brandsDefinitionStore.publicBrands) {
			setPublicBrands(
				brandsDefinitionStore.publicBrands.items.filter((item) => item.status === CampaignEntityStatus.ACTIVE),
			);
		}
	};

	useEffect(() => {
		loadBrands();

		campaignStore.isFirstTimeBrandApplied = true;
		if (!isPopupView) campaignStore.originalLogoObjects = {};
	}, []);
	useEffect(() => {
		setInternalBrands();
	}, [brandsDefinitionStore.brands, brandsDefinitionStore.publicBrands]);

	const loadBrands = (forcefetchBrands: boolean = false) => {
		if ((brandsDefinitionStore.brands.items.length === 0 && !brandsDefinitionStore.isLoading) || forcefetchBrands) {
			brandsDefinitionStore.loadBrandsDefinition();
		} else {
			setInternalBrands();
		}
		if (brandsDefinitionStore.publicBrands.items.length === 0 && !brandsDefinitionStore.isLoadingPublicBrands) {
			brandsDefinitionStore.loadPublicBrandsDefinition();
		} else {
			setInternalPublicBrands();
		}
	};

	const addNewBrand = () => {
		brandsDefinitionStore.setProperty("selectedBrand", defaultBrandDefinition);
		setShowSelectedBrand(true);
		setSelectedBrand(defaultBrandDefinition);
	};

	useEffect(() => {
		const ApplySelectedBrand = async () => {
			if (!isViewIconClicked && activeBrand) {
				brandsDefinitionStore.setProperty("isApplyingBrandConfigurations", true);
				const scenes = await updateScenes();
				let defaultLoadedScene = scenes[0];
				const mainSceneInfo = scenes.reduce<{ index: number; scene: IScene } | null>((acc, scene, index) => {
					return scene.isMainScene ? { index, scene } : acc;
				}, null);
				if (isPopupView) {
					if (mainSceneInfo?.scene) {
						defaultLoadedScene = mainSceneInfo.scene;
					}
				}
				if (brandsDefinitionStore.globalSelectedBrand) {
					brandsDefinitionStore.setProperty("hasBrandReset", false);
					if (isPopupView) {
						if (!editor) return;
						defaultLoadedScene = await applyBrandConfigrationOnScene(
							brandsDefinitionStore.globalSelectedBrand,
							defaultLoadedScene,
							mainSceneInfo?.index ?? 0,
						);
						const updatedPreview = (await editor?.renderer.render(defaultLoadedScene)) as string;
						const updatedScene = { ...defaultLoadedScene, preview: updatedPreview, duration: 1000 };
						const updatedScenes = scenes.map((scene: IScene) => {
							if (scene.id === updatedScene.id) {
								return { ...updatedScene, preview: updatedPreview };
							}
							return editor.scene.formalizeSceneAttributes(scene);
						}) as IScene[];
						setScenes(updatedScenes);
						designEditorStore.setProperty("isSceneFullyLoaded", false);
						setCurrentScene(updatedScene);
					} else {
						await applyBrandConfigration(brandsDefinitionStore.globalSelectedBrand, scenes);
					}
				}
				brandsDefinitionStore.setProperty("isApplyingBrandConfigurations", false);
				campaignStore.isFirstTimeBrandApplied = false;
			}
		};

		ApplySelectedBrand();
	}, [isViewIconClicked, brandsDefinitionStore.globalSelectedBrand, activeBrand]);

	const handleSelectBrand = async (brand: BrandDefinition) => {
		const selectedBrand = brand;
		setSelectedBrand(selectedBrand);
		brandsDefinitionStore.setProperty("selectedBrand", selectedBrand);
	};

	const handleShowBrand = () => {
		setShowSelectedBrand(true);
	};

	const handleShowBrandsList = () => {
		setShowSelectedBrand(false);
	};

	const handleTextFieldChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		brandsDefinitionStore.formErrors.invalidName = false;
		const newValue = e.target.value;
		brandsDefinitionStore.handleBrandChange("name", newValue);
		setSelectedBrand((prevBrand) => {
			if (prevBrand) {
				return {
					...prevBrand,
					name: newValue,
				};
			} else {
				return null;
			}
		});
	};

	return (
		<Box className={styles.brandContainer}>
			<Box className={styles.tapContainer}>
				{!showSelectedBrand ? (
					<MainTabHeader title={t("title")} />
				) : (
					<Box className={styles.subHeader}>
						<Box className={styles.title}>
							<InputLayout
								className={styles.brandNameInput}
								showLabel={false}
								validateOn={!brandsDefinitionStore.selectedBrand.name}
								validationMessage={t("brandNameRequired")}
							>
								<TextField
									required
									variant="outlined"
									value={selectedBrand?.name}
									onChange={(e) => handleTextFieldChange(e)}
									placeholder={t("brandTitle")}
									fullWidth
									InputProps={{
										classes: {
											root: clsx(styles.brandNameTextField, {
												[styles.readOnly]: !isBrandTemplateEditor,
											}),
										},
									}}
									error={brandsDefinitionStore.formErrors.invalidName}
									helperText={brandsDefinitionStore.formErrors.invalidName && t("invalidName")}
								/>
							</InputLayout>
						</Box>
						<Box className={styles.iconsContainer}>
							{isBrandTemplateEditor && (
								<BriaIconButton className={styles.editIcon}>
									<EditIcon />
								</BriaIconButton>
							)}

							<BriaIconButton
								onClick={() => {
									if (
										brandsDefinitionStore.selectedBrand?.org_id === BRIA_PUBLIC_ORG_ID &&
										getSelectedOrganization()?.organization.uid != BRIA_PUBLIC_ORG_ID
									) {
										setShowSelectedBrand(false);
										loadBrands(false);
									} else {
										setShowBrandSavingPopup(true);
									}
								}}
							>
								<ChevronLeft />
							</BriaIconButton>
						</Box>
					</Box>
				)}
			</Box>

			<Box
				className={clsx(styles.tabs, styles.brandTab, {
					[styles.maxHeightInPopView]: isPopupView,
				})}
			>
				{showSelectedBrand ? (
					<EditBrand
						isBrandTemplateEditor={isBrandTemplateEditor}
						loadBrands={loadBrands}
						handleShowBrandsList={handleShowBrandsList}
						setSuccessMessage={setSuccessMessage}
						setShowBrandSavingPopup={setShowBrandSavingPopup}
						showBrandSavingPopup={showBrandSavingPopup}
						setShowSelectedBrand={setShowSelectedBrand}
					/>
				) : (
					<LoadingPlaceholder
						className={styles.circleLoader}
						size="25px"
						isLoading={brandsDefinitionStore.isLoading || brandsDefinitionStore.isLoadingPublicBrands}
					>
						<div className={styles.brandsContainer}>
							<div>
								{!isPopupView && (
									<Typography className={styles.brandsDescription}>
										{t("brandsDescription")}
									</Typography>
								)}

								{/* Public Brands */}
								{isPopupView && publicBrands.length > 0 && (
									<>
										<Typography className={styles.sectionTitle}>{t("publicBrands")}</Typography>
										{publicBrands.map((brand: BrandDefinition) => (
											<BrandItem
												isBrandTemplateEditor={isBrandTemplateEditor}
												key={brand.id}
												brand={brand}
												onClick={handleSelectBrand}
												selectedBrand={selectedBrand}
												handleShowBrand={handleShowBrand}
												loadBrands={loadBrands}
												setIsViewIconClicked={setIsViewIconClicked}
												setActiveBrand={setActiveBrand}
												activeBrand={activeBrand}
												isBrandFromPublicOrg={
													brand.org_id === BRIA_PUBLIC_ORG_ID &&
													getSelectedOrganization()?.organization.uid != BRIA_PUBLIC_ORG_ID
												}
											/>
										))}
									</>
								)}

								{/* Private Brands */}
								{brands.length > 0 && (
									<>
										{isPopupView && (
											<Typography className={styles.sectionTitle}>
												{t("privateBrands")}
											</Typography>
										)}
										{brands.map((brand: BrandDefinition) => (
											<BrandItem
												isBrandTemplateEditor={isBrandTemplateEditor}
												key={brand.id}
												brand={brand}
												onClick={handleSelectBrand}
												selectedBrand={selectedBrand}
												handleShowBrand={handleShowBrand}
												loadBrands={loadBrands}
												setIsViewIconClicked={setIsViewIconClicked}
												setActiveBrand={setActiveBrand}
												activeBrand={activeBrand}
											/>
										))}
									</>
								)}
							</div>
							{!isPopupView && isBrandTemplateEditor ? (
								<>
									<BriaTooltip title={t("continueWithoutABrandTooltip")}>
										<Box
											className={clsx(styles.continueWithoutABrand, {
												[styles.disabled]: !campaignStore.selectedTemplate,
												[styles.selected]:
													campaignStore.selectedTemplate &&
													!campaignStore.selectedTemplate.is_branded_template,
											})}
											onClick={() => {
												if (campaignStore.selectedTemplate)
													campaignStore.selectedTemplate.is_branded_template = false;
											}}
										>
											<Typography>{t("continueWithoutABrand")}</Typography>
										</Box>
									</BriaTooltip>

									<Box
										className={clsx(styles.addNewBrand, {
											[styles.disabled]: brandsDefinitionStore.isApplyingBrandConfigurations,
										})}
										onClick={addNewBrand}
									>
										<Box className={styles.newBrandIcon}>
											<PlusIcon />
										</Box>
										<Typography>{t("addNew")}</Typography>
									</Box>
								</>
							) : !isPopupView ? (
								<>
									<Box className={styles.adminModeNotice}>
										<Typography>{t("adminModeNotice.part1")}</Typography>
										<Typography className={styles.bold}>{t("adminModeNotice.part2")}</Typography>
									</Box>
								</>
							) : null}
							{isPopupView && designEditorStore.activeStep === BRANDS_INDEX && <StepperNavigation />}
						</div>
					</LoadingPlaceholder>
				)}
			</Box>
			<Snackbar
				autoHideDuration={2000}
				open={!!successMessage}
				onClose={() => setSuccessMessage(null)}
				className={styles.saveSuccessToast}
			>
				<Alert severity="success">{successMessage}</Alert>
			</Snackbar>
		</Box>
	);
};
const ObservedComponent = observer(Brands);
export default ObservedComponent;
